<template>
  <template v-if="!loading && metadataLoaded">
    <refonte-card>
      <factures-filters
        v-if="metadataLoaded"
        v-bind:metadatas="metadatas" />
    </refonte-card>
    <refonte-card class="mt-[8px] !gap-0 !p-0">
      <div
        class="flex flex-wrap items-center gap-[8px] px-[12px] pb-[10px] pt-[12px]">
        <refonte-checkbox
          label="Tout sélectionner"
          v-on:input="(value) => selectAllRows(value)"
          class="!self-center" />
        <IconSeparatorVertical />
        <div class="flex flex-wrap items-start gap-[8px]">
          <refonte-button
            v-bind:disabled="!hasFacturesSelected"
            v-bind:icon_left="IconDownload"
            v-on:click="downloadFactures">
            <div class="inline-flex items-center gap-[5px]">
              Télécharger
              <IconBills />
            </div>
          </refonte-button>
        </div>
        <IconSeparatorVertical />
        <refonte-table-number
          v-if="totalElement || totalElement == 0"
          v-bind:number="totalElement"
          v-bind:label="totalElement > 1 ? 'Factures' : 'Facture'" />
        <IconSeparatorVertical />
        <refonte-table-number
          v-bind:number="factureCount?.nbFacturesARegler"
          v-if="
            factureCount?.nbFacturesARegler ||
            factureCount?.nbFacturesARegler == 0
          "
          v-bind:label="`${
            factureCount?.nbFacturesARegler > 1 ? 'Factures' : 'Facture'
          } à régler`"
          v-bind:on_click="
            () => {
              factureFilterStore.setFilter(
                FACTURES_FILTERS_CONFIGURATION.typeIds.name,
                [3]
              );
            }
          " />
      </div>
      <refonte-separator class="px-[12px]" />
      <div class="mt-2 max-h-[70vh] overflow-y-auto">
        <factures-content v-if="!dataLoading" />
        <template v-else>
          <spinner
            sizes="w-20 h-20"
            thickness="border-8" />
        </template>
        <refonte-load-more
          v-bind:show_load="page + 1 != totalPages"
          v-bind:load="load"
          v-bind:total_of_elements="totalElement"
          v-bind:loading="showLoadMore"
          v-bind:number_of_elements="factures.length" />
      </div>
    </refonte-card>
  </template>
  <template v-else>
    <spinner
      sizes="w-20 h-20"
      thickness="border-8" />
  </template>
</template>

<script setup lang="ts">
  import IconSeparatorVertical from '@/assets/img/refonte/icons/display/icon-separator-vertical.svg?component';
  import RefonteCard from '@/components/refonte/cards/refonte-card.vue';
  import RefonteCheckbox from '@/components/refonte/inputs/refonte-checkbox.vue';
  import RefonteSeparator from '@/components/refonte/separators/refonte-separator.vue';
  import { onMounted, ref, watch } from 'vue';
  import Spinner from '@/components/utils/spinner.vue';
  import FacturesContent from '@/views/_espace/_refonte/gestion/_factures/factures-content.vue';
  import {
    FACTURES_FILTERS_CONFIGURATION,
    useFactureFilterStore,
    useFactureStore
  } from '@/stores/factures';
  import { storeToRefs } from 'pinia';
  import { useEntrepriseStore } from '@/stores/entreprise';
  import RefonteTableNumber from '@/components/refonte/numbers/refonte-table-number.vue';
  import { useRoute, useRouter } from 'vue-router';
  import api_utils from '@/mixin/api_utils';
  import FacturesFilters from '@/views/_espace/_refonte/gestion/_factures/factures-filters.vue';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import IconDownload from '@/assets/img/refonte/icons/display/icon-download.svg?component';
  import IconBills from '@/assets/img/refonte/icons/menu/icon-bills.svg?component';
  import { Filters } from '@/types/gestion/inscriptions-filters-types';
  import filters_utils from '@/mixin/utils/filters_utils';
  import http_functions from '@/mixin/http_functions';
  import {
    FactureCount,
    FactureMetadata
  } from '@/types/gestion/factures-filters-types';
  import { API } from '@/http-common';
  import RefonteLoadMore from '@/components/refonte/inputs/refonte-load-more.vue';

  const loading = ref(false);

  const entrepriseStore = useEntrepriseStore();
  const { activeClientIds } = storeToRefs(entrepriseStore);
  const factureFilterStore = useFactureFilterStore();

  const { filters } = storeToRefs(factureFilterStore);

  const dataLoading = ref<boolean>(false);
  const factureStore = useFactureStore();
  const {
    hasFacturesSelected,
    getFacturesSelected,
    page,
    totalPages,
    factures,
    totalElement
  } = storeToRefs(factureStore);

  const selectAllRows = (value: boolean) => {
    factureStore.selectAllFactureItems(value);
  };
  const fetchData = async () => {
    dataLoading.value = true;
    await fetchCount(route.query);
    await factureStore.fetchFactures(route.query);
    dataLoading.value = false;
  };
  const route = useRoute();
  watch(
    () => route.query,
    async () => {
      await updateFilters();
    }
  );

  const updateFilters = async () => {
    factureFilterStore.updateFiltersFromQuery(route.query);
  };

  const initFilters = async () => {
    factureFilterStore.initFiltersFromQuery(route.query);
  };

  onMounted(async () => {
    loading.value = true;
    await initFilters();
    await fetchMetadata();
    loading.value = false;
  });

  const router = useRouter();

  watch(filters, async () => {
    let query = getQuery(filters.value);
    if (activeClientIds.value && activeClientIds.value != '') {
      query = {
        ...query,
        clientIds: activeClientIds.value
      };
    }
    if (filters_utils.methods.isDeepEqual(query, route.query)) {
      await fetchData();
    } else {
      await router.push({ query });
    }
  });
  const showLoadMore = ref(false);
  const load = async () => {
    showLoadMore.value = true;
    await factureStore.loadMore(route.query);
    showLoadMore.value = false;
  };

  const getQuery = (filters: Filters) =>
    api_utils.methods.formatParams(filters, [
      FACTURES_FILTERS_CONFIGURATION.date.name,
      FACTURES_FILTERS_CONFIGURATION.datePeriodeDebut.name,
      FACTURES_FILTERS_CONFIGURATION.datePeriodeFin.name,
      FACTURES_FILTERS_CONFIGURATION.dateFormation.name,
      FACTURES_FILTERS_CONFIGURATION.dateFormationPeriodeDebut.name,
      FACTURES_FILTERS_CONFIGURATION.dateFormationPeriodeFin.name
    ]);
  const downloadFactures = () => {
    const facturesIds = getFacturesSelected.value.map((f) => f.id).join(',');
    http_functions.methods.downloadFile(
      `client/v2/factures/zip?factureIds=${facturesIds}`,
      'factures.zip'
    );
  };

  const factureCount = ref<FactureCount>();
  const fetchCount = async (query: Filters) => {
    const response = await API.get('client/v2/factures/count', {
      params: query
    });

    if (response && response.data) {
      factureCount.value = response.data;
    }
  };
  const metadatas = ref<FactureMetadata>({
    types: [],
    references: [],
    commandes: [],
    vendeurs: [],
    participants: [],
    formations: []
  });
  const metadataLoaded = ref<boolean>(false);
  const fetchMetadata = async () => {
    metadataLoaded.value = false;
    void fetchMetadataTypes();
    void fetchMetadataReferences();
    void fetchMetadataCommandes();
    void fechtMetadataParticipants();
    void fetchMetadataFormations();
    void fetchMetadataVendeurs();
    metadataLoaded.value = true;
  };

  const fetchMetadataTypes = async () => {
    const response = await API.get(
      `client/v2/factures/metadata/types${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.types = response.data ?? [];
    }
  };

  const fetchMetadataReferences = async () => {
    const response = await API.get(
      `client/v2/factures/metadata/references${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.references = response.data ?? [];
    }
  };

  const fetchMetadataCommandes = async () => {
    const response = await API.get(
      `client/v2/factures/metadata/commandes${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.commandes = response.data ?? [];
    }
  };

  const fetchMetadataFormations = async () => {
    const response = await API.get(
      `client/v2/factures/metadata/formations${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.formations = response.data ?? [];
    }
  };

  const fechtMetadataParticipants = async () => {
    const response = await API.get(
      `client/v2/factures/metadata/participants${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.participants = response.data ?? [];
    }
  };

  const fetchMetadataVendeurs = async () => {
    const response = await API.get(
      `client/v2/factures/metadata/vendeurs${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.vendeurs = response.data ?? [];
    }
  };
</script>

<style lang="scss" scoped></style>
